import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  UserCheck,
  UserX,
  Users,
  Search,
  Loader2,
  Building,
  Mail,
  Phone,
  MapPin,
  Clock,
  Check,
  X
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

function AgentApprovals() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(function() {
    loadPendingUsers();
  }, []);

  async function loadPendingUsers() {
    try {
      const response = await api.get('/users/pending');
      setPendingUsers(response.data);
    } catch (err) {
      console.error('Failed to load pending users:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(userId) {
    setProcessingId(userId);
    try {
      await api.post('/users/' + userId + '/approve', {
        user_id: userId,
        action: 'approve'
      });
      toast.success('User approved successfully');
      loadPendingUsers();
    } catch (err) {
      toast.error('Failed to approve user');
    } finally {
      setProcessingId(null);
    }
  }

  async function handleReject(userId) {
    setProcessingId(userId);
    try {
      await api.post('/users/' + userId + '/approve', {
        user_id: userId,
        action: 'reject',
        rejection_reason: rejectionReason || null
      });
      toast.success('User rejected');
      setRejectingId(null);
      setRejectionReason('');
      loadPendingUsers();
    } catch (err) {
      toast.error('Failed to reject user');
    } finally {
      setProcessingId(null);
    }
  }

  const filteredUsers = pendingUsers.filter(function(user) {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return user.email.toLowerCase().includes(term) ||
           user.first_name.toLowerCase().includes(term) ||
           user.last_name.toLowerCase().includes(term) ||
           user.company?.toLowerCase().includes(term) ||
           user.country?.toLowerCase().includes(term);
  });

  return (
    <div className="space-y-6" data-testid="agent-approvals">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Pending approvals</h1>
          <p className="text-gray-600 mt-1">Review and approve customer registrations</p>
        </div>
        <Badge className="bg-amber-100 text-amber-800 text-lg px-4 py-2">
          <Clock className="h-5 w-5 mr-2" />
          {pendingUsers.length} Pending
        </Badge>
      </div>

      {/* Search */}
      {pendingUsers.length > 0 && (
        <div className="bg-white rounded-sm shadow-card p-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by name, email, company, or country..."
              className="pl-12 h-12 rounded-sm"
              value={searchTerm}
              onChange={function(e) { setSearchTerm(e.target.value); }}
            />
          </div>
        </div>
      )}

      {/* Pending Users List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : filteredUsers.length > 0 ? (
        <div className="space-y-4">
          {filteredUsers.map(function(user) {
            const isRejecting = rejectingId === user.id;
            
            return (
              <div key={user.id} className="bg-white rounded-sm shadow-card overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-amber-100 rounded-sm flex items-center justify-center">
                        <Users className="h-7 w-7 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-ink text-lg">
                          {user.first_name} {user.last_name}
                        </h3>
                        <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
                          <Mail className="h-4 w-4" />
                          <span>{user.email}</span>
                        </div>
                        {user.company && (
                          <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
                            <Building className="h-4 w-4" />
                            <span>{user.company}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="h-4 w-4 text-blue-600" />
                            <span className="text-ink font-medium">{user.country}</span>
                            {user.city && <span className="text-gray-400">• {user.city}</span>}
                          </div>
                          {user.phone && (
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <Phone className="h-4 w-4" />
                              <span>{user.phone}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          Registered {formatDate(user.created_at)}
                        </p>
                      </div>
                    </div>

                    {!isRejecting && (
                      <div className="flex gap-2">
                        <Button
                          className="bg-green-600 hover:bg-green-700 rounded-full"
                          onClick={function() { handleApprove(user.id); }}
                          disabled={processingId === user.id}
                        >
                          {processingId === user.id ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <>
                              <Check className="h-5 w-5 mr-2" />
                              Approve
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          className="rounded-full text-red-500 border-red-200 hover:bg-red-50"
                          onClick={function() { setRejectingId(user.id); }}
                        >
                          <X className="h-5 w-5 mr-2" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Rejection Form */}
                  {isRejecting && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">Reason for rejection (optional):</p>
                      <Textarea
                        value={rejectionReason}
                        onChange={function(e) { setRejectionReason(e.target.value); }}
                        placeholder="Provide a reason for rejecting this registration..."
                        className="rounded-sm mb-3"
                        rows={3}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={function() { setRejectingId(null); setRejectionReason(''); }}
                        >
                          Cancel
                        </Button>
                        <Button
                          className="bg-red-600 hover:bg-red-700 rounded-full"
                          onClick={function() { handleReject(user.id); }}
                          disabled={processingId === user.id}
                        >
                          {processingId === user.id ? (
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          ) : (
                            <UserX className="h-5 w-5 mr-2" />
                          )}
                          Confirm Rejection
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-sm shadow-card p-16 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <UserCheck className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-normal tracking-[-0.01em] text-ink mb-2">All Caught Up!</h2>
          <p className="text-gray-500">No pending registrations to review</p>
        </div>
      )}
    </div>
  );
}

export default AgentApprovals;
